import { useState, type ComponentPropsWithoutRef } from "react";

import { cn } from "@/lib/utils";

/**
 * An image, or -- while the file is not there yet -- a labelled slot the size of one.
 *
 * The guide's screenshots are added by hand as they are taken, into `public/` under the
 * path the caller names. The page must read correctly before then, and the placeholder shows
 * the author where each one goes and what it needs to show. A root-relative `src` follows the
 * deployment's base path.
 */
export function Screenshot({ src, alt, title, className }: ComponentPropsWithoutRef<"img">) {
  const [missing, setMissing] = useState(false);
  const base = import.meta.env.BASE_URL.replace(/\/$/, "");
  const href = src?.startsWith("/") ? `${base}${src}` : src;
  if (missing) {
    return (
      <span
        role="img"
        aria-label={alt}
        className={cn(
          "text-muted-foreground bg-muted/40 flex min-h-28 items-center justify-center rounded-md border border-dashed px-4 text-center text-xs",
          className,
        )}
      >
        {alt}
      </span>
    );
  }
  return (
    <img
      src={href}
      alt={alt}
      title={title}
      loading="lazy"
      onError={() => setMissing(true)}
      className={cn("max-w-full rounded-md border", className)}
    />
  );
}
